import { type StreamEvent, StreamEventSchema } from './stream.ts'

/**
 * Server-Sent Events framing, shared by the writer (API) and the reader (web client) so
 * the two can never drift.
 *
 * Ralph Chat streams over `fetch` + POST rather than `EventSource`, because EventSource
 * cannot send a request body, cannot set headers (so no CSRF token), and cannot be
 * cancelled cleanly. The framing is still standard SSE so any proxy that understands
 * `text/event-stream` -- and every corporate one does -- keeps the connection open.
 */

export const SSE_CONTENT_TYPE = 'text/event-stream'

/** `id:` carries the durable seq so a reconnect can ask for everything after it. */
export const encodeSseEvent = (event: StreamEvent): string => {
  const payload = JSON.stringify(event)
  return `id: ${event.seq}\nevent: ${event.type}\ndata: ${payload}\n\n`
}

export const SSE_COMMENT_KEEPALIVE = ': keep-alive\n\n'

export type SseParseResult = { events: StreamEvent[]; rest: string; malformed: number }

/**
 * Incremental SSE frame parser. Returns the leftover partial frame so the caller can
 * prepend it to the next chunk -- a naive `split('\n\n')` per chunk silently drops any
 * event that straddles a TCP boundary, which is exactly the long ones that matter.
 */
export const parseSseChunk = (buffer: string): SseParseResult => {
  const frames = buffer.split('\n\n')
  const rest = frames.pop() ?? ''
  const events: StreamEvent[] = []
  let malformed = 0

  for (const frame of frames) {
    const dataLines = frame
      .split('\n')
      .filter((line) => line.startsWith('data:'))
      .map((line) => line.slice(5).trimStart())

    if (dataLines.length === 0) continue // comment / keep-alive frame

    try {
      const parsed = StreamEventSchema.safeParse(JSON.parse(dataLines.join('\n')))
      if (parsed.success) events.push(parsed.data)
      else malformed += 1
    } catch {
      malformed += 1
    }
  }

  return { events, rest, malformed }
}
